import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail, avatarSrc, formatCurrency, toImageSrc } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAppWebSocket } from "@/lib/useAppWebSocket";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { PaperPlaneTilt, ChatsCircle, ArrowLeft } from "@phosphor-icons/react";

export default function MessagesPage() {
  const { user } = useAuth();
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // selected other user object
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);
  const activeIdRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/messages");
      setThreads(data);
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
      return [];
    } finally {
      setLoadingThreads(false);
    }
  }, []);

  const openConversation = useCallback(async (otherId, push = true) => {
    activeIdRef.current = otherId;
    try {
      const { data } = await api.get(`/messages/${otherId}`);
      if (activeIdRef.current !== otherId) return;
      setActive(data.user);
      setMessages(data.messages);
      setTimeout(() => {
        if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
      }, 30);
      // Refresh threads to clear unread badge
      loadThreads();
      if (push) navigate(`/messages/${otherId}`, { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to open");
    }
  }, [loadThreads, navigate]);

  // Initial load
  useEffect(() => {
    (async () => {
      const ts = await loadThreads();
      const targetId = agentId || (ts[0] && ts[0].user.id);
      if (targetId) openConversation(targetId, false);
    })();
  }, []); // eslint-disable-line

  // If agentId in URL changes (e.g. clicked Message from Network), open it
  useEffect(() => {
    if (agentId && agentId !== activeIdRef.current) openConversation(agentId, false);
  }, [agentId, openConversation]);

  // WebSocket: live message events refresh the open conversation + thread list
  useAppWebSocket(useCallback((data) => {
    if (data?.type !== "message.new" || !data.message) return;
    const m = data.message;
    const other = activeIdRef.current;
    // If the new message belongs to the open conversation, append it locally
    const belongs = other && (m.from_id === other || m.to_id === other);
    if (belongs) {
      setMessages((prev) => {
        if (prev.some((x) => x.id === m.id)) return prev;
        setTimeout(() => { if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight; }, 30);
        return [...prev, m];
      });
      // Mark received messages from the other party as read on the server
      if (m.from_id === other) {
        api.get(`/messages/${other}`).catch(() => {});
      }
    }
    loadThreads();
  }, [loadThreads]), !!user && typeof user === "object");

  // Polling fallback every 15s (longer interval now that WS handles realtime)
  useEffect(() => {
    const id = setInterval(async () => {
      if (activeIdRef.current) {
        try {
          const { data } = await api.get(`/messages/${activeIdRef.current}`);
          if (activeIdRef.current === data.user?.id) {
            setMessages((prev) => {
              if (data.messages.length !== prev.length) {
                setTimeout(() => { if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight; }, 30);
              }
              return data.messages;
            });
          }
        } catch {}
      }
      loadThreads();
    }, 15000);
    return () => clearInterval(id);
  }, [loadThreads]);

  const send = async () => {
    if (!text.trim() || !active) return;
    setSending(true);
    const draft = text;
    setText("");
    try {
      const { data } = await api.post(`/messages/${active.id}`, { content: draft });
      setMessages((m) => [...m, data]);
      setTimeout(() => { if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight; }, 30);
      loadThreads();
    } catch (e) {
      setText(draft);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send");
    } finally { setSending(false); }
  };

  return (
    <div
      className="h-[calc(100dvh-3.5rem-60px)] md:h-screen flex flex-col md:flex-row overflow-hidden"
      data-testid="messages-page"
    >
      {/* Threads list */}
      <aside className={`border-r hairline w-full md:w-80 shrink-0 bg-ink-900/40 ${active ? "hidden md:flex" : "flex"} flex-col min-h-0`}>
        <div className="px-5 pt-8 pb-4 border-b hairline">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-1">Direct</div>
          <h1 className="font-display text-3xl tracking-tight">Messages</h1>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="threads-list">
          {loadingThreads ? (
            <div className="p-4 space-y-3" data-testid="threads-skeleton">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3 p-2">
                  <Skeleton className="h-10 w-10 rounded-full bg-white/[0.08]" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3 w-1/2 bg-white/[0.08]" />
                    <Skeleton className="h-3 w-4/5 bg-white/[0.05]" />
                  </div>
                </div>
              ))}
            </div>
          ) : threads.length === 0 ? (
            <div className="text-zinc-500 text-sm p-6">
              No conversations yet. Visit an agent's profile and tap "Message".
            </div>
          ) : (
            threads.map((t) => (
              <button
                key={t.user.id}
                onClick={() => openConversation(t.user.id)}
                data-testid={`thread-${t.user.id}`}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b hairline transition-colors ${
                  active?.id === t.user.id ? "bg-white/[0.04]" : "hover:bg-white/[0.02]"
                }`}
              >
                <Avatar className="h-10 w-10 border border-white/10 shrink-0">
                  <AvatarImage src={avatarSrc(t.user.avatar_url)} />
                  <AvatarFallback className="bg-ink-700 text-xs">{t.user.name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm truncate">{t.user.name}</div>
                    {t.unread > 0 && (
                      <span className="text-[9px] uppercase tracking-wider bg-gold text-ink-900 rounded-full px-1.5 py-0.5 font-bold" data-testid={`unread-${t.user.id}`}>
                        {t.unread}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">
                    {t.last_message.from_id === user?.id ? "You: " : ""}
                    {t.last_message.attachment_type === "property"
                      ? <span>📎 Property{t.last_message.content ? ` · ${t.last_message.content}` : ""}</span>
                      : t.last_message.content}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </aside>

      {/* Conversation */}
      <section className={`flex-1 flex flex-col min-w-0 min-h-0 ${active ? "flex" : "hidden md:flex"}`}>
        {active ? (
          <>
            <div className="border-b hairline px-5 py-4 flex items-center gap-3" data-testid="conversation-header">
              <button onClick={() => { setActive(null); activeIdRef.current = null; navigate("/messages", { replace: true }); }} className="md:hidden text-zinc-400 hover:text-white" data-testid="back-to-threads">
                <ArrowLeft size={18} />
              </button>
              <Avatar className="h-10 w-10 border border-white/10">
                <AvatarImage src={avatarSrc(active.avatar_url)} />
                <AvatarFallback className="bg-ink-700">{active.name?.[0]}</AvatarFallback>
              </Avatar>
              <div>
                <div className="text-sm">{active.name}</div>
                <div className="text-xs text-zinc-500">{active.specialty} · {active.area}{active.city ? `, ${active.city}` : ""}</div>
              </div>
            </div>

            <div ref={scrollerRef} className="flex-1 overflow-y-auto min-h-0 p-5 space-y-2" data-testid="messages-scroller">
              {messages.length === 0 ? (
                <div className="text-zinc-500 text-sm text-center py-10">Say hi to {active.name?.split(" ")[0]} 👋</div>
              ) : (
                messages.map((m) => {
                  const mine = m.from_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-gold text-ink-900 rounded-tr-sm" : "bg-ink-700 text-zinc-100 rounded-tl-sm"}`}>
                        {m.content && <div className="text-sm whitespace-pre-wrap">{m.content}</div>}
                        {m.attachment_type === "property" && m.attachment && (
                          <PropertyAttachmentCard prop={m.attachment} mine={mine} />
                        )}
                        <div className={`text-[10px] mt-1 ${mine ? "text-ink-900/60" : "text-zinc-500"}`}>{formatTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="border-t hairline p-3 flex gap-2 items-center shrink-0 bg-ink-900/60 backdrop-blur" data-testid="composer">
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={`Message ${active.name?.split(" ")[0]}…`}
                className="bg-ink-700 border-white/10"
                data-testid="message-input"
                autoFocus
              />
              <Button onClick={send} disabled={sending || !text.trim()} className="bg-gold text-ink-900 hover:bg-gold-light" data-testid="send-message">
                <PaperPlaneTilt size={14} weight="fill" />
              </Button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-zinc-500">
            <div className="text-center">
              <ChatsCircle size={48} className="mx-auto text-zinc-700 mb-3" />
              <div className="text-sm">Select a conversation or message an agent from their profile.</div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function PropertyAttachmentCard({ prop, mine }) {
  if (!prop) return null;
  const cover = prop.image_url || prop.images?.[0];
  return (
    <a
      href={`/p/${prop.id}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`property-attachment-${prop.id}`}
      className={`mt-2 block rounded-lg overflow-hidden border ${mine ? "border-ink-900/20" : "border-white/10"} ${mine ? "bg-ink-900/10" : "bg-ink-800"} no-underline`}
      style={{ minWidth: 240 }}
    >
      {cover && (
        <img src={toImageSrc(cover)} alt={prop.title} className="w-full h-28 object-cover" />
      )}
      <div className="p-2.5">
        <div className={`text-xs ${mine ? "text-ink-900/90" : "text-zinc-100"} font-medium truncate`}>{prop.title}</div>
        <div className={`text-[11px] mt-0.5 ${mine ? "text-ink-900/60" : "text-zinc-500"} truncate`}>{prop.location || ""}</div>
        <div className="flex items-center justify-between mt-1.5">
          <div className={`font-mono text-sm ${mine ? "text-ink-900" : "text-gold"}`}>{formatCurrency(prop.price)}</div>
          <div className={`text-[10px] uppercase tracking-wider ${mine ? "text-ink-900/60" : "text-zinc-500"}`}>
            {prop.listing_for === "rent" ? "Rent" : "Sale"}{prop.bedrooms ? ` · ${prop.bedrooms} BHK` : ""}
          </div>
        </div>
      </div>
    </a>
  );
}

